import { useState } from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import {
  ProjectCostChartPointsDocument,
  ProjectManagementProjectDetailsPageOverviewPageProjectFragment,
} from 'generated/resources/graphql'
import { compact } from 'lodash'
import moment, { Moment } from 'moment'

import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { overviewFrequencyOptionsEnum } from 'modules/project_management/lib/constants'
import {
  formatGraphTimeTitle,
  formatYAxisWithLargeNumbers,
} from 'modules/project_management/lib/formatChartComponents'
import { OverviewFilterOption } from 'modules/project_management/lib/overviewOptions'

import { ChartInsight } from '../../ChartInsight'
import { ResponsiveLineChart } from '../../ResponsiveLineChart'
import { ConstantTransformedData, EvolutionTransformedData } from '../getEvolutionTransformedData'

interface CostEvolutionChartProps {
  projectId: number
  startDate?: Moment
  endDate?: Moment
  fixedCostPlanning?: ProjectManagementProjectDetailsPageOverviewPageProjectFragment['fixedCostPlanning']
  selectedFrequency: overviewFrequencyOptionsEnum
  selectedFilter: OverviewFilterOption
  currency: string
}

export const CostEvolutionChart: React.FC<CostEvolutionChartProps> = ({
  projectId,
  startDate = moment().startOf('week'),
  endDate = moment().endOf('week'),
  fixedCostPlanning,
  selectedFrequency,
  selectedFilter,
  currency,
}) => {
  const [evolutionDataShown, setEvolutionDataShown] = useState<boolean>(true)

  const onChangeEvolutionDataShown = (isShown: boolean) => {
    setEvolutionDataShown(isShown)
  }

  const { data, loading, error } = useQuery(ProjectCostChartPointsDocument, {
    variables: {
      projectId: projectId,
      startDate: startDate.format('YYYY-MM-DD'),
      endDate: endDate.format('YYYY-MM-DD'),
      slotType: selectedFrequency,
    },
  })

  const costChartPoints = data?.projectManagement?.projectCostChartPoints || []

  const costData = costChartPoints.map((item, index) => {
    return {
      x: index,
      y: item.costCents ? item.costCents / 100 : 0,
      title: index !== 0 ? formatGraphTimeTitle(item.year, item.timeSlot, selectedFrequency) : '',
    }
  })

  const plannedCost: ConstantTransformedData = {
    id: 'planned_cost',
    label: i18n.t('project_management.graphs.evolution.cost.legends.planned_cost'),
    value: fixedCostPlanning?.budgetAllocationCents
      ? fixedCostPlanning.budgetAllocationCents / 100
      : 0,
    color: 'grey800',
  }

  const plannedIncome: ConstantTransformedData = {
    id: 'planned_income',
    label: i18n.t('project_management.graphs.evolution.cost.legends.planned_income'),
    value: fixedCostPlanning?.feeAmountCents ? fixedCostPlanning?.feeAmountCents / 100 : 0,
    color: 'viridian1000',
  }

  const firstItem = costChartPoints[0]
  const lastItem = costChartPoints[costChartPoints.length - 1]

  const min = firstItem?.costCents ? firstItem?.costCents / 100 : 0
  const max = lastItem?.costCents ? lastItem?.costCents / 100 : 0

  const registeredCostData: EvolutionTransformedData = {
    id: 'registered_cost',
    label: i18n.t('project_management.graphs.evolution.cost.legends.registered_cost'),
    max: max,
    min: min,
    data: costData,
    color: 'tangerine1000',
  }

  const constantData = compact([
    fixedCostPlanning?.budgetAllocationCents ? plannedCost : null,
    fixedCostPlanning?.feeAmountCents ? plannedIncome : null,
  ])

  const formatYAxis = (value: number) => {
    return formatYAxisWithLargeNumbers(value, currency)
  }

  return (
    <Box gap="s12">
      <ChartInsight
        id={'registered_cost'}
        title={selectedFilter.label}
        startingValue={evolutionDataShown ? registeredCostData?.min : undefined}
        endingValue={evolutionDataShown ? registeredCostData?.max : undefined}
        color={evolutionDataShown ? 'tangerine1000' : 'grey400'}
        currency={currency}
      />
      <Text color="grey800" weight="medium" size="300">
        {selectedFilter.subtitle}
      </Text>
      <ResponsiveLineChart
        evolutionData={registeredCostData}
        constantData={constantData}
        unit={currency}
        formatYAxis={formatYAxis}
        onChangeEvolutionDataShown={onChangeEvolutionDataShown}
        selectedFrequency={selectedFrequency}
        loading={loading}
        error={error}
      />
    </Box>
  )
}
